import { type FC } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { CardGridArea, Tag } from "widgets";

import { type User } from "../../graphql.js";
import { type IMarkerState } from "../../redux/modules/marker.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";
import { MarkerCard } from "./cards/MarkerCard.js";
import { MarkerDetailsCard } from "./cards/MarkerDetailsCard.js";
import { ReferencesCard } from "./cards/ReferencesCard.js";
import { RegionsOfContigCard } from "./cards/RegionsOfContigCard.js";
import RelatedProjectsCard from "./cards/RelatedProjectsCard.js";

interface IMarkerSummaryViewProps {
  title?: string;
  headerTags?: JSX.Element[];
}

const MarkerSummaryView: FC<IMarkerSummaryViewProps> = (props) => {
  const { marker, isLoading } = useSelector<IAppState, IMarkerState>((state) => state.marker, shallowEqual);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (marker ? marker.name : "")}
      subtitle={marker?.description || "Marker Summary"}
      entity={marker}
      refGenomes={undefined}
      headerTags={[<Tag label={marker?.markerType.name} key={"marker-summary-view-tag-1"} />]}
      headerAttributes={[
        <AttributesListWidget
          attributes={[
            marker?.synonyms?.length ? { label: "Synonyms", description: marker.synonyms.join(", ") } : null,
          ]}
          key={"marker-summary-view-widget-2"}
        />,
      ]}
      isLoading={isLoading}
      getEntityName={() => marker?.name}
    >
      {marker && (
        <>
          <MarkerCard marker={marker} area={CardGridArea.Left} />
          <ReferencesCard references={marker.externalReferences} area={CardGridArea.Right} />
          <MarkerDetailsCard marker={marker} area={CardGridArea.Left} />
          {marker.regions?.length > 0 && (
            <RegionsOfContigCard entity={marker} regions={marker.regions} area={CardGridArea.Right} />
          )}
          <RelatedProjectsCard
            relatedProjects={marker.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default MarkerSummaryView;
